import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { eatDayOf, eatTimeOf, formatEatDateTime, formatIsoDay } from '@wodi/domain';
import { shaApprovalText, type TheatreCaseView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { cancelCase, rescheduleCase, theatreCaseQuery, theatreListQuery } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { ChargesSection } from './charges-section.tsx';
import { ChecklistSection } from './checklist-section.tsx';
import { OperationNoteSection } from './operation-note-section.tsx';
import styles from './theatre.module.css';
import { errorText, STATUS_TAGS, URGENCY_WORDS } from './words.ts';

// One theatre case (docs/plans/phase2-theatre.md §6): the booking, what holds it, the safety
// checklist step by step, the operation note and the theatre charges.

const route = getRouteApi('/_app/theatre/cases/$caseId');
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

const GATE_WORDS: Partial<Record<TheatreCaseView['gate'], string>> = {
  waiting_for_payment:
    'The procedure is waiting for payment. Sign in is held until the cashier records payment.',
  not_priced: 'The procedure has no price yet. Ask the cashier to price it before sign in.',
};

function clock(iso: string): string {
  const t = eatTimeOf(new Date(iso));
  return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
}

function RescheduleDialog({ view, onClose }: { view: TheatreCaseView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const list = useQuery(theatreListQuery(null));
  const [theatreId, setTheatreId] = useState<string | null>(view.theatre.id);
  const [day, setDay] = useState<string | null>(
    formatIsoDay(eatDayOf(new Date(view.scheduledStart))),
  );
  const [time, setTime] = useState(clock(view.scheduledStart));
  const [minutes, setMinutes] = useState(String(view.expectedMinutes));
  const [reason, setReason] = useState('');
  const count = Number(minutes);
  const minutesOk = Number.isInteger(count) && count >= 1 && count <= 1440;
  const move = useMutation({
    mutationFn: () =>
      rescheduleCase(view.id, {
        theatreId: theatreId ?? '',
        scheduledStart: new Date(`${day ?? ''}T${time}:00+03:00`).toISOString(),
        expectedMinutes: count,
        reason: reason.trim(),
      }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({
        title: 'Case rescheduled',
        description: `${c.theatre.name}, ${formatEatDateTime(new Date(c.scheduledStart))}`,
      });
      onClose();
    },
  });
  const ready =
    online &&
    theatreId !== null &&
    day !== null &&
    TIME.test(time) &&
    minutesOk &&
    reason.trim().length >= 5;
  return (
    <Dialog
      title="Reschedule case"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Select
        label="Theatre"
        options={(list.data?.theatres ?? [view.theatre]).map((t) => ({ id: t.id, label: t.name }))}
        value={theatreId}
        onChange={(v) => {
          setTheatreId(typeof v === 'string' ? v : null);
        }}
      />
      <div className={styles.row}>
        <DateField label="Date" value={day} onChange={setDay} />
        <TextField label="Start time" value={time} onChange={setTime} width="sm" />
        <TextField
          label="Expected length"
          unit="min"
          inputMode="numeric"
          value={minutes}
          onChange={setMinutes}
          width="sm"
        />
      </div>
      <TextArea
        label="Why is the case moving?"
        description="At least 5 characters. Kept in the audit log."
        value={reason}
        onChange={setReason}
        rows={2}
      />
      {move.error === null ? null : (
        <Notice tone="error">{errorText(move.error, "The case wasn't moved. Try again.")}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={move.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            move.mutate();
          }}
        >
          Reschedule case
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CancelDialog({ view, onClose }: { view: TheatreCaseView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [withRequest, setWithRequest] = useState(false);
  const cancel = useMutation({
    mutationFn: () =>
      cancelCase(view.id, { reason: reason.trim(), cancelProcedureRequest: withRequest }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({
        title: 'Case cancelled',
        description:
          result.procedureCharge === 'needs_credit_note'
            ? 'The procedure is already on a bill: ask for a credit note'
            : withRequest
              ? 'The procedure request is cancelled too'
              : 'The procedure request stays open for rebooking',
      });
      onClose();
    },
  });
  return (
    <Dialog
      title="Cancel case"
      tone="alert"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextArea label="Why is the case cancelled?" value={reason} onChange={setReason} />
      <Checkbox isSelected={withRequest} onChange={setWithRequest}>
        Also cancel the procedure request, which won't be rebooked
      </Checkbox>
      {cancel.error === null ? null : (
        <Notice tone="error">
          {errorText(cancel.error, "The case wasn't cancelled. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep case
        </Button>
        <Button
          variant="destructive"
          isDisabled={!online || reason.trim().length < 5}
          isPending={cancel.isPending}
          pendingLabel="Cancelling…"
          onPress={() => {
            cancel.mutate();
          }}
        >
          Cancel case
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TheatreCasePage() {
  const { caseId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const theatreCase = useQuery(theatreCaseQuery(caseId));
  const online = useOnline();
  const [open, setOpen] = useState<'reschedule' | 'cancel' | null>(null);

  if (theatreCase.isPending) return <LoadingBlock label="Loading the case" rows={8} />;
  if (theatreCase.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void theatreCase.refetch()}>Try again</Button>}
      >
        {errorText(theatreCase.error, "The case couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const c = theatreCase.data;
  const allowed = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);
  const [statusWord, statusShape] = STATUS_TAGS[c.status];
  const start = new Date(c.scheduledStart);
  const gateWords = c.status === 'booked' ? GATE_WORDS[c.gate] : undefined;
  const approvalHolds =
    c.status === 'booked' &&
    c.approval !== null &&
    ['needs_request', 'request_failed', 'waiting', 'declined'].includes(c.approval.state);
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={c.patientId} />
      <PageHeader
        title="Theatre case"
        description={`${c.procedure.name}, ${c.theatre.name}, ${formatEatDateTime(start)}`}
        actions={
          c.status === 'booked' && allowed('theatre.book') ? (
            <>
              <Button
                isDisabled={!online}
                onPress={() => {
                  setOpen('reschedule');
                }}
              >
                Reschedule
              </Button>
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setOpen('cancel');
                }}
              >
                Cancel case
              </Button>
            </>
          ) : undefined
        }
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Recording the checklist, the note and charges waits for the
          connection.
        </Notice>
      ) : null}
      {gateWords === undefined ? null : <Notice tone="info">{gateWords}</Notice>}
      {approvalHolds && c.approval !== null ? (
        <Notice tone="info">
          {`${shaApprovalText(c.approval)}. Sign in is held until SHA approves. Ask SHA for approval from the visit's orders; a senior clinician may go ahead with a reason when SHA can't answer.`}
        </Notice>
      ) : null}
      <Section title="Booking" actions={<Tag state={statusShape}>{statusWord}</Tag>}>
        <dl className={styles.facts}>
          <dt>Procedure</dt>
          <dd>
            <span className={`wodi-id ${styles.code}`}>{c.procedure.code}</span>
            {c.procedure.name}
          </dd>
          <dt>Theatre</dt>
          <dd>{c.theatre.name}</dd>
          <dt>When</dt>
          <dd>{`${formatEatDateTime(start)}, ${String(c.expectedMinutes)} minutes`}</dd>
          <dt>Surgeon</dt>
          <dd>{c.surgeon.displayName}</dd>
          <dt>Urgency</dt>
          <dd>{URGENCY_WORDS[c.urgency]}</dd>
          <dt>Visit</dt>
          <dd>
            <span className="wodi-id">{c.visit.visitNumber}</span>
            {c.visit.admissionId === null ? null : (
              <>
                {' '}
                <Link to="/admissions/$admissionId" params={{ admissionId: c.visit.admissionId }}>
                  Open stay
                </Link>
              </>
            )}
          </dd>
          <dt>Booked</dt>
          <dd>{`${formatEatDateTime(new Date(c.bookedAt))} by ${c.bookedBy.displayName}`}</dd>
          {c.approval === null ? null : (
            <>
              <dt>SHA approval</dt>
              <dd>{shaApprovalText(c.approval)}</dd>
            </>
          )}
          {c.startedAt === null ? null : (
            <>
              <dt>In theatre</dt>
              <dd>
                {`${formatEatDateTime(new Date(c.startedAt))}${c.endedAt === null ? '' : ` to ${formatEatDateTime(new Date(c.endedAt))}`}`}
              </dd>
            </>
          )}
          {c.bookingNote === null ? null : (
            <>
              <dt>Booking note</dt>
              <dd className={styles.prose}>{c.bookingNote}</dd>
            </>
          )}
          {c.cancelReason === null ? null : (
            <>
              <dt>Cancelled because</dt>
              <dd>{c.cancelReason}</dd>
            </>
          )}
        </dl>
      </Section>
      <Section
        title="Safety checklist"
        description="Pre-op, then the WHO steps sign in, time out and sign out, each recorded once with who and when."
      >
        <ChecklistSection
          view={c}
          mayRecord={allowed('theatre.checklist.record')}
          mayUpload={allowed('documents.upload')}
        />
      </Section>
      <Section title="Operation note">
        <OperationNoteSection view={c} mayWrite={allowed('theatre.note.write')} />
      </Section>
      <Section title="Theatre charges">
        <ChargesSection view={c} mayRecord={allowed('theatre.charges.record')} />
      </Section>
      <p className={styles.meta}>
        <Link to="/theatre">Back to the theatre list</Link>
      </p>
      {open === 'reschedule' ? (
        <RescheduleDialog
          view={c}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'cancel' ? (
        <CancelDialog
          view={c}
          onClose={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </div>
  );
}
